import {
  CreateButton,
  Datagrid,
  FunctionField,
  Record,
  TextField,
  useListContext,
  useTranslate,
} from "react-admin";
import fuelProviders from ".";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import { FuelProviderDto } from "../../../common/data/service-proxies";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { Box, Tooltip, Typography, useTheme, Theme } from "@material-ui/core";

import "../../common/styles/list-style.css";
import { ActionsField } from "../../common/data-grid-fields/ActionsField";
import { DeleteRecordMenuItem } from "../../common/data-grid-fields/DeleteRecordMenuItem";
import { useAppData } from "../../../common/hooks/useAppData";

interface Props {}

const formatTimestamp = (
  value: string | null | undefined,
  timeZone: string | null | undefined,
  neverLabel: string
) =>
  value
    ? `${new Date(value).toLocaleString(undefined, {
        timeZone: timeZone ?? "UTC",
      })} ${timeZone ?? "UTC"}`
    : neverLabel;

const ProviderStatus = ({
  record,
  theme,
  timeZone,
  translate,
}: {
  record?: FuelProviderDto;
  theme: Theme;
  timeZone?: string | null;
  translate: (key: string) => string;
}) => {
  const isActive = record?.status === "Active";
  const isDegraded = isActive && (record?.consecutiveFailureCount ?? 0) > 0;
  const isAutoDisabled = !isActive && !!record?.autoDisabledUtc;
  const label = isAutoDisabled
    ? translate("resources.admin-fuel-providers.labels.autoDisabledBySystem")
    : isDegraded
    ? translate("resources.admin-fuel-providers.labels.degraded")
    : isActive
    ? translate("resources.common.labels.active")
    : translate("resources.admin-fuel-providers.labels.inactive");
  const timestamp = isAutoDisabled
    ? record?.autoDisabledUtc
    : isDegraded
    ? record?.nextAttemptUtc
    : null;

  return (
    <Box display="flex" alignItems="flex-start">
      {isDegraded ? (
        <ErrorOutlineIcon style={{ color: theme.palette.warning.main }} />
      ) : isActive ? (
        <CheckBoxIcon style={{ color: theme.palette.success.main }} />
      ) : (
        <IndeterminateCheckBoxIcon
          style={{ color: theme.palette.error.main }}
        />
      )}
      <Box ml={1} minWidth={0}>
        <Typography variant="body2">{label}</Typography>
        {timestamp && (
          <Typography variant="caption" display="block">
            {translate(
              isDegraded
                ? "resources.admin-fuel-providers.labels.nextAttempt"
                : "resources.admin-fuel-providers.labels.disabled"
            )}
            :{" "}
            {formatTimestamp(
              timestamp,
              timeZone,
              translate("resources.admin-fuel-providers.labels.never")
            )}
          </Typography>
        )}
        {record?.lastFailureReason && (isDegraded || isAutoDisabled) && (
          <Tooltip title={record.lastFailureReason}>
            <Typography
              variant="caption"
              display="block"
              noWrap
              style={{ maxWidth: 280 }}
            >
              {record.lastFailureReason}
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const ListActions = () => {
  const { basePath } = useListContext();
  const permissions = useActionPermissions();
  return (
    <>
      {permissions &&
        permissions[permissionNames.admin.fuelProviders.main] &&
        permissions[permissionNames.admin.fuelProviders.create] && (
          <CreateButton basePath={basePath} />
        )}
    </>
  );
};

export const FuelProvidersList = (props: Props) => {
  const permissions = useActionPermissions();
  const theme: Theme = useTheme();
  const translate = useTranslate();
  const timeZone = useAppData((x) => x.timeZoneInfo?.ianaTimeZone);

  return (
    <div className="content-container">
      <DefaultProLogsList
        empty={<ProLogsEmpty iconType={fuelProviders.icon} />}
        className="content-list"
        title="resources.admin-fuel-providers.name"
        bulkActionButtons={false}
        actions={<ListActions />}
        {...props}
      >
        <Datagrid>
          <TextField
            source="provider"
            label="resources.admin-fuel-providers.labels.provider"
          />
          <TextField
            source="name"
            label="resources.admin-fuel-providers.labels.name"
          />
          <FunctionField<FuelProviderDto & Record>
            source="status"
            headerClassName="centered-content"
            label="resources.common.labels.active"
            render={(record) => (
              <ProviderStatus
                record={record}
                theme={theme}
                timeZone={timeZone}
                translate={translate}
              />
            )}
          />
          <FunctionField<FuelProviderDto & Record>
            source="lastSuccessUtc"
            label="resources.admin-fuel-providers.labels.lastSuccessfulSync"
            render={(record) =>
              formatTimestamp(
                record?.lastSuccessUtc,
                timeZone,
                translate("resources.admin-fuel-providers.labels.never")
              )
            }
          />
          <ActionsField
            editable={() =>
              permissions &&
              permissions[permissionNames.admin.fuelProviders.main] &&
              permissions[permissionNames.admin.fuelProviders.update]
            }
            canActivateOrDeactivate={() =>
              permissions &&
              permissions[permissionNames.admin.fuelProviders.main] &&
              permissions[permissionNames.admin.fuelProviders.update]
            }
          >
            {permissions &&
              permissions[permissionNames.admin.fuelProviders.main] &&
              permissions[permissionNames.admin.fuelProviders.delete] && (
                <DeleteRecordMenuItem />
              )}
          </ActionsField>
        </Datagrid>
      </DefaultProLogsList>
    </div>
  );
};
